import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import SiteShell from "../components/SiteShell";
import HoverChat from "../components/HoverChat";
import Action from "../components/Action";
import { COLUMNS, ROWS, cellAt } from "./dharshana/cells";

/**
 * The Vision (`/vision` canonical; `/dharshana` alias).
 *
 * Structure:
 *   1. Hero row: H1 + body paragraph
 *   2. Two-image row: vertically-integrated + horizontally-distributed
 *   3. Section header: "The Whole Picture: Three axes, four scales, twelve doors."
 *   4. Full-width tabs (three axes) → each tab reveals the axis intro
 *      followed by four stacked scale sub-sections
 *      (Individual · Community · Institutional · Civilisational).
 *
 * The former left-column "Scale" nav was removed — scales are now
 * stacked as anchored sub-sections inside each tab so nothing is lost.
 *
 * Hash sync: `/vision#{scale}-{axis}` deep-links pre-select the axis tab
 * AND scroll to the matching scale sub-section on mount.
 * Old `/dharshana/{col-row}` URLs redirect here from Dharshana.jsx.
 */

const SCALES = ROWS; // four scales from cells.js
const AXES = COLUMNS; // three axes (green/gold/grid)

const AXIS_INTROS = {
  green: {
    lead: "Clean energy, made and stored here.",
    body: (
      <>
        A 720 MWh battery factory. Lithium-ion today, Sodium-ion tomorrow for
        cathode — both paired with{" "}
        <HoverChat term="coconut-shell-hard-carbon">
          Hard Carbon anode made of coconut shells
        </HoverChat>{" "}
        from Tamil Nadu and Kerala. From coconut shell to battery cell, every
        step earns its margin in a single factory. This is being built now
        inside a co-owned SPV whose{" "}
        <Action route="request-data-room" source="vision-green-intro">
          data room you can request
        </Action>
        .
      </>
    ),
  },
  gold: {
    lead: "Wealth that stays with the people who own it.",
    body: (
      <>
        A battery that only works is a warehouse; a battery that is owned is an
        inheritance. Through cooperatives on the{" "}
        <HoverChat term="aavin">Aavin</HoverChat> pattern and the{" "}
        <HoverChat term="gcpp">Group Captive</HoverChat> framework, citizens
        and industries hold ≥ 26% of the assets that power them. The returns
        come back as bill credits, dividends, and landed power costs that fall
        from ₹8-12 to ₹4-5.50 a unit. If you run a plant exposed to{" "}
        <HoverChat term="cbam">CBAM</HoverChat> or diesel, you can{" "}
        <Action route="gcpp-model" source="vision-gold-intro">
          model exactly what captive ownership saves you
        </Action>
        .
      </>
    ),
  },
  grid: {
    lead: "The intelligence that makes it all work.",
    body: (
      <>
        A battery without a brain is a guess.{" "}
        <HoverChat term="griddata">Griddata</HoverChat> is a Made-in-India
        operating system that makes every panel, battery, meter and substation
        visible and dispatchable on one screen — settling fractional
        cooperative ownership, running the{" "}
        <HoverChat term="vpp">virtual power plant</HoverChat>, and built for
        Indian tariffs, payment rails and data law. Ready to digitally
        transform your substation?{" "}
        <Action
          route="book-tour"
          source="vision-grid-intro"
          queryParams={{ visit_type: "griddata-pilot" }}
        >
          Apply for the Griddata pilot program
        </Action>
        .
      </>
    ),
  },
};

// Parse a `/vision#{scale}-{axis}` hash. Fallback to null on any parse
// failure so a bad external link still lands on defaults.
function parseHash(hash) {
  const cleaned = (hash || "").replace(/^#/, "");
  if (!cleaned) return null;
  const [maybeScale, maybeAxis] = cleaned.split("-");
  const validScale = SCALES.some((s) => s.id === maybeScale);
  const validAxis = AXES.some((a) => a.id === maybeAxis);
  if (!validScale || !validAxis) return null;
  return { scale: maybeScale, axis: maybeAxis };
}

export default function Vision() {
  const location = useLocation();
  const navigate = useNavigate();

  // Initial selection from hash, or default to `green`.
  const parsed = parseHash(location.hash);
  const [axis, setAxis] = useState(parsed?.axis || "green");

  // Keep tab in sync when hash changes externally (browser back/forward,
  // redirected URLs from /dharshana/{col-row}). Also scroll the specific
  // scale sub-section into view after the tab content has rendered.
  useEffect(() => {
    const p = parseHash(location.hash);
    if (!p) return;
    setAxis(p.axis);
    // Wait past the AnimatePresence tab-swap transition (250ms) and any
    // native hash-scroll the browser may have attempted before React
    // mounted. Then compute the target's absolute Y and scroll explicitly,
    // offsetting the sticky header (~90px on desktop).
    const HEADER_OFFSET = 90;
    const attempt = (retries = 6) => {
      const el = document.getElementById(`${p.scale}-${p.axis}`);
      if (el) {
        const y = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
        window.scrollTo({ top: y, left: 0, behavior: "auto" });
        return;
      }
      if (retries > 0) setTimeout(() => attempt(retries - 1), 80);
    };
    setTimeout(attempt, 320);
  }, [location.hash]);

  // Update the hash silently on tab click without adding to history.
  // We anchor tab clicks to the `civilisational` scale by default so the
  // hash always parses correctly; the visible scroll position doesn't
  // change on tab click (only on hash-driven navigation).
  const onAxis = useCallback(
    (nextAxis) => {
      setAxis(nextAxis);
      const parsedNow = parseHash(location.hash);
      const scaleForHash = parsedNow?.scale || "individual";
      const nextHash = `#${scaleForHash}-${nextAxis}`;
      if (location.hash !== nextHash) {
        navigate({ hash: nextHash }, { replace: true });
      }
    },
    [location.hash, navigate],
  );

  const activeAxis = useMemo(
    () => AXES.find((a) => a.id === axis),
    [axis],
  );

  return (
    <SiteShell>
      {/* Canonical hint for search engines — /dharshana points here. */}
      <link rel="canonical" href="https://gg-assets-review.emergent.host/vision" />

      {/* ============ Hero ============ */}
      <section
        data-testid="vision-hero"
        style={{
          maxWidth: 1180,
          margin: "0 auto",
          padding:
            "clamp(48px, 7vw, 96px) clamp(20px, 5vw, 60px) clamp(24px, 4vw, 48px)",
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="gg-eyebrow" style={{ marginBottom: 14 }}>
            The Vision
          </div>
          <h1
            className="gg-h1"
            data-testid="vision-h1"
            style={{
              fontFamily: "Fraunces, Georgia, serif",
              fontSize: "clamp(30px, 4.6vw, 56px)",
              lineHeight: 1.15,
              letterSpacing: "-0.01em",
              maxWidth: "22ch",
              margin: 0,
            }}
          >
            The Vision: Create a{" "}
            <span className="gg-gradient-text">Vertically Integrated</span>{" "}
            and <span className="gg-gradient-text">Horizontally Distributed</span>{" "}
            Energy Ecosystem.
          </h1>
          <p
            data-testid="vision-body"
            style={{
              marginTop: 28,
              maxWidth: 780,
              color: "var(--text-cloud)",
              fontSize: "clamp(15.5px, 1.5vw, 18px)",
              lineHeight: 1.7,
            }}
          >
            We're building energy the way it should work: integrated end-to-end,
            and owned by everyone. Each GreenGold node controls its full value
            chain, from solar generation and 720 MWh of storage to the
            intelligence that runs it, so nothing is lost to middlemen. Then we
            make that node repeatable: a standardized factory anyone can
            license, owned by the community it powers, and connected to one
            shared brain that grows smarter with every site. Deep by design.
            Distributed by values.
          </p>
        </motion.div>

        {/* ============ Two images row ============ */}
        <div
          data-testid="vision-images-row"
          className="gg-vision-images"
          style={{
            marginTop: "clamp(40px, 6vw, 72px)",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "clamp(18px, 3vw, 36px)",
          }}
        >
          {/* placeholder — swap when client provides real asset. */}
          <VisionImageCard
            src="/images/vertically-integrated.png"
            caption="Vertically Integrated"
            testId="vision-image-vi"
          />
          {/* placeholder — swap when client provides real asset. */}
          <VisionImageCard
            src="/images/horizontally-distributed.png"
            caption="Horizontally Distributed"
            testId="vision-image-hd"
          />
        </div>
      </section>

      {/* ============ Doors section ============ */}
      <section
        data-testid="vision-doors-section"
        style={{
          maxWidth: 1180,
          margin: "0 auto",
          padding:
            "clamp(48px, 7vw, 96px) clamp(20px, 5vw, 60px) clamp(60px, 8vw, 100px)",
          borderTop: "1px solid var(--line)",
        }}
      >
        <h2
          className="gg-h2"
          data-testid="vision-doors-heading"
          style={{
            fontFamily: "Fraunces, Georgia, serif",
            fontSize: "clamp(24px, 3vw, 38px)",
            lineHeight: 1.2,
            marginBottom: 34,
            maxWidth: "22ch",
          }}
        >
          The Whole Picture:{" "}
          <span className="gg-gradient-text">
            Three axes, four scales, twelve doors.
          </span>
        </h2>

        {/* Full-width tab row */}
        <div
          role="tablist"
          aria-label="Axis"
          data-testid="vision-axis-tabs"
          style={{
            display: "flex",
            gap: 6,
            borderBottom: "1px solid var(--line)",
            marginBottom: 32,
            flexWrap: "wrap",
          }}
        >
          {AXES.map((a) => {
            const active = a.id === axis;
            return (
              <button
                key={a.id}
                role="tab"
                type="button"
                onClick={() => onAxis(a.id)}
                data-testid={`vision-axis-${a.id}`}
                data-active={active ? "true" : "false"}
                aria-selected={active}
                style={{
                  padding: "12px 22px",
                  border: "none",
                  background: "transparent",
                  color: active ? "var(--paper)" : "var(--text-mute)",
                  fontFamily: "Fraunces, Georgia, serif",
                  fontSize: 18,
                  cursor: "pointer",
                  borderBottom: `2px solid ${active ? a.accent : "transparent"}`,
                  marginBottom: -1,
                  transition: "color 0.2s, border-color 0.2s",
                }}
              >
                {a.label}
              </button>
            );
          })}
        </div>

        {/* Axis intro + stacked scale sub-sections */}
        <AnimatePresence mode="wait">
          <motion.div
            key={axis}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.25 }}
          >
            {/* Axis intro */}
            <div
              data-testid={`vision-axis-intro-${axis}`}
              style={{ marginBottom: 40 }}
            >
              <div
                className="gg-eyebrow"
                style={{ marginBottom: 10, color: activeAxis.accent }}
              >
                The {activeAxis.label} axis
              </div>
              <p
                style={{
                  fontFamily: "Fraunces, Georgia, serif",
                  fontSize: "clamp(20px, 2.2vw, 26px)",
                  lineHeight: 1.35,
                  color: "var(--paper)",
                  marginBottom: 16,
                  maxWidth: "40ch",
                }}
              >
                {AXIS_INTROS[axis].lead}
              </p>
              <p
                style={{
                  color: "var(--text-dim)",
                  fontSize: 16,
                  lineHeight: 1.75,
                  maxWidth: "68ch",
                }}
              >
                {AXIS_INTROS[axis].body}
              </p>
            </div>

            {/* Four scale sub-sections stacked vertically */}
            <div
              data-testid={`vision-scales-list-${axis}`}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 20,
              }}
            >
              {SCALES.map((s) => {
                const cell = cellAt(axis, s.id);
                const anchorId = `${s.id}-${axis}`;
                return (
                  <section
                    key={anchorId}
                    id={anchorId}
                    data-testid={`vision-cell-${s.id}-${axis}`}
                    className="gg-glass"
                    style={{
                      padding: "clamp(22px, 3vw, 30px)",
                      scrollMarginTop: 100,
                    }}
                  >
                    <div
                      className="gg-eyebrow"
                      style={{ marginBottom: 8, color: activeAxis.accent }}
                    >
                      Scale
                    </div>
                    <h3
                      data-testid={`vision-scale-heading-${s.id}-${axis}`}
                      style={{
                        fontFamily: "Fraunces, Georgia, serif",
                        fontSize: "clamp(18px, 1.9vw, 22px)",
                        lineHeight: 1.25,
                        margin: "0 0 4px",
                        color: "var(--gold-soft)",
                        letterSpacing: "-0.005em",
                      }}
                    >
                      {s.label}
                    </h3>
                    {cell ? (
                      <>
                        <div
                          className="gg-eyebrow"
                          style={{
                            marginTop: 10,
                            marginBottom: 10,
                            color: "var(--text-mute)",
                            fontSize: 10.5,
                          }}
                        >
                          {activeAxis.label} · {cell.audience}
                        </div>
                        <h4
                          style={{
                            fontFamily: "Fraunces, Georgia, serif",
                            fontSize: "clamp(20px, 2.2vw, 26px)",
                            lineHeight: 1.3,
                            margin: "0 0 14px",
                            color: "var(--paper)",
                            maxWidth: "34ch",
                          }}
                        >
                          {cell.title}
                        </h4>
                        <p
                          style={{
                            color: "var(--text-cloud)",
                            fontSize: 16,
                            lineHeight: 1.75,
                            marginBottom: 18,
                            maxWidth: "62ch",
                          }}
                        >
                          {cell.narrative}
                        </p>
                        <p
                          style={{
                            color: "var(--text-cloud)",
                            fontSize: 15.5,
                            lineHeight: 1.7,
                            margin: 0,
                            maxWidth: "62ch",
                          }}
                        >
                          {cell.action.pre}
                          <Action
                            route={cell.action.route}
                            source={`vision-cell-${s.id}-${axis}`}
                            queryParams={cell.action.queryParams}
                          >
                            {cell.action.linkText}
                          </Action>
                          {cell.action.post}
                        </p>
                      </>
                    ) : (
                      <p style={{ color: "var(--text-dim)", marginTop: 10 }}>
                        Content coming soon.
                      </p>
                    )}
                  </section>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>

        <style>{`
          @media (max-width: 720px) {
            .gg-vision-images { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </section>
    </SiteShell>
  );
}

function VisionImageCard({ src, caption, testId }) {
  return (
    <figure
      data-testid={testId}
      style={{
        margin: 0,
        borderRadius: 18,
        overflow: "hidden",
        border: "1px solid var(--line-strong)",
        background: "rgba(10,22,40,0.4)",
        boxShadow: "0 20px 40px -30px rgba(0,0,0,0.9)",
      }}
    >
      <img src={src} alt={caption} style={{ width: "100%", height: "auto", display: "block" }} />
      <figcaption
        style={{
          padding: "14px 18px",
          fontFamily: "Fraunces, Georgia, serif",
          fontSize: 17,
          color: "var(--gold-soft)",
          borderTop: "1px solid var(--line)",
          background: "rgba(0,0,0,0.35)",
        }}
      >
        {caption}
      </figcaption>
    </figure>
  );
}
